import { parseISO } from 'date-fns';

import { Country, usaName } from 'common/src/constants/countries';
import { healZipPostalCodeValue } from 'common/src/transformers/healZipPostalCodeValue';
import { FacilityType } from 'common/src/types/enums/FacilityType.enum';
import { IFacility, IFacilityGroup } from 'common/src/types/interfaces';

import {
  getAddressFields,
  getStartAndEndDateFields,
} from 'portal/src/components/common/forms/commonFormFields';
import getInternalNotesField from 'portal/src/components/common/forms/internalNotesField';
import { getLocationFormFields } from 'portal/src/components/common/forms/locationFormFields';

import {
  autoCompleteType,
  datePickerType,
  noFieldType,
  numberType,
  patternInputType,
  selectInputType,
  switchType,
  textInputType,
} from '../../../common/forms/FormFieldTypes';

export type FacilityFormValues = ReturnType<typeof defaultValues>;

export const defaultValues = (entity: Partial<IFacility> = {}) => ({
  address1: entity.address1 ?? '',
  address2: entity.address2 ?? '',
  city: entity.city ?? '',
  cookingFacilities: !!entity.cookingFacilities,
  country: (entity.country || usaName) as Country,
  dateClosed: entity.dateClosed ? parseISO(entity.dateClosed) : null,
  dateOpened: entity.dateOpened ? parseISO(entity.dateOpened) : null,
  facilityGroups: entity.facilityGroups?.map((group) => group.id) || [],
  internalNotes: entity.internalNotes ?? '',
  // Use undefined to force a value when adding K-12 Schools
  highschool:
    typeof entity.highschool === 'boolean' ? entity.highschool : undefined,
  irrigatedSquareFootage: entity.irrigatedSquareFootage ?? 0,
  latitude: entity.latitude ?? '',
  longitude: entity.longitude ?? '',
  name: entity.name ?? '',
  notes: entity.notes ?? '',
  number: entity.number ?? '',
  percentCooled: entity.percentCooled ?? 0,
  percentHeated: entity.percentHeated ?? 0,
  percentOperational: entity.percentOperational ?? 0,
  postalCode: healZipPostalCodeValue(entity.postalCode, entity.country) ?? '',
  squareFootage: entity.squareFootage ?? 0,
  state: entity.state ?? '',
  // Use undefined to force a value when adding K-12 Schools or Colleges
  studentOccupants: entity.studentOccupants ?? undefined,
  timezone: entity.timezone ?? '',
  totalOccupants: entity.totalOccupants ?? 0,
  type: entity.type ?? '',
  weekendOperations: !!entity.weekendOperations,
  yearBuilt: entity.yearBuilt ?? '',
});

export const getFields = (
  facilityGroups: IFacilityGroup[],
  type: string,
  onSqFtChange: (value: number) => void,
  onStudentOcptsChange: (value: number) => void,
  onTotalOcptsChange: (value: number) => void,
  openSqFootHistModal: (() => void) | undefined,
  openStudentOcptsHistModal: (() => void) | undefined,
  openTotalOcptsHistModal: (() => void) | undefined,
  country: Country,
  hasSystemAdminAccess: boolean,
) => {
  const formFieldsConfiguration: Array<
    | autoCompleteType
    | datePickerType
    | noFieldType
    | numberType
    | patternInputType
    | selectInputType
    | switchType
    | textInputType
  > = [
    {
      placeholder: 'Facility Name',
      label: 'Facility Name',
      name: 'name',
      type: 'textInput',
      required: true,
    },
    {
      placeholder: 'Facility Type',
      label: 'Facility Type',
      name: 'type',
      type: 'selectInput',
      required: true,
      values: Object.values(FacilityType).map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      placeholder: 'Facility Groups',
      label: 'Facility Groups',
      name: 'facilityGroups',
      type: 'selectInput',
      required: false,
      multiple: true,
      values: facilityGroups.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    },
    {
      placeholder: 'Facility Number',
      label: 'Facility Number',
      name: 'number',
      type: 'textInput',
      required: false,
    },
    ...getAddressFields(true, false, country),
    {
      placeholder: 'Year Built',
      label: 'Year Built',
      name: 'yearBuilt',
      type: 'number',
      thousandSeparator: false,
      min: 0,
      required: true,
    },
    {
      placeholder: 'Number of Student Occupants',
      label: 'Number of Student Occupants',
      name: 'studentOccupants',
      type: 'number',
      min: 0,
      onChange: onStudentOcptsChange,
      required: type === FacilityType.Kto12 || type === FacilityType.College,
      hidden: type !== FacilityType.Kto12 && type !== FacilityType.College,
      helperAction: openStudentOcptsHistModal
        ? {
            label: 'See changes',
            onClick: () => openStudentOcptsHistModal(),
          }
        : null,
    },
    {
      type: 'noField',
      name: 'noField',
      hidden: type === FacilityType.Kto12 || type === FacilityType.College,
    },
    {
      placeholder: 'High School',
      label: 'High School',
      name: 'highschool',
      type: 'selectInput',
      required: type === FacilityType.Kto12,
      hidden: type !== FacilityType.Kto12,
      values: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      validate: (value) => value === true || value === false,
    },
    {
      type: 'noField',
      name: 'noField',
      hidden: type !== FacilityType.Kto12,
    },
    ...getStartAndEndDateFields({
      startFieldName: 'dateOpened',
      endFieldName: 'dateClosed',
      startLabel: 'Date Opened',
      endLabel: 'Date Closed',
    }),
    {
      placeholder: 'Total Occupants',
      label: 'Total Occupants',
      name: 'totalOccupants',
      type: 'number',
      min: 0,
      onChange: onTotalOcptsChange,
      required: false,
      helperAction: openTotalOcptsHistModal
        ? {
            label: 'See changes',
            onClick: () => openTotalOcptsHistModal(),
          }
        : null,
    },
    {
      placeholder: 'Percent Operational',
      label: 'Percent Operational',
      name: 'percentOperational',
      type: 'number',
      min: 0,
      max: 100,
      required: false,
      suffix: ' %',
    },
    {
      placeholder: 'Percent Heated',
      label: 'Percent Heated',
      name: 'percentHeated',
      type: 'number',
      min: 0,
      max: 100,
      required: false,
      suffix: ' %',
    },
    {
      placeholder: 'Percent Cooled',
      label: 'Percent Cooled',
      name: 'percentCooled',
      type: 'number',
      min: 0,
      max: 100,
      required: false,
      suffix: ' %',
    },
    {
      placeholder: 'Square Footage',
      label: 'Square Footage',
      name: 'squareFootage',
      type: 'number',
      min: 0,
      onChange: onSqFtChange,
      required: false,
      suffix: ' sqft',
      helperAction: openSqFootHistModal
        ? {
            label: 'See changes',
            onClick: () => openSqFootHistModal(),
          }
        : null,
    },
    {
      placeholder: 'Irrigated Square Footage',
      label: 'Irrigated Square Footage',
      name: 'irrigatedSquareFootage',
      type: 'number',
      min: 0,
      required: false,
      suffix: ' sqft',
    },
    {
      label: 'Does this facility have cooking facilities?',
      name: 'cookingFacilities',
      type: 'switch',
      required: false,
    },
    {
      label: 'Does this facility have weekend operations?',
      name: 'weekendOperations',
      type: 'switch',
      required: false,
    },
    ...getLocationFormFields(),
    {
      placeholder: 'Notes',
      label: 'Notes',
      multiline: true,
      name: 'notes',
      type: 'textInput',
      required: false,
    },
    getInternalNotesField(hasSystemAdminAccess),
  ];
  return formFieldsConfiguration;
};
